import React, { useState, useRef, useEffect } from 'react';
import {
  User,
  Settings,
  LogOut,
  Menu,
  X,
  Wallet,
  BarChart3,
  ChevronUp,
  ChevronDown,
  Users,
  Tag,
  Receipt,
  ArrowRightLeft,
  Plus,
  Repeat
} from 'lucide-react';
import ReceiptScanModal from './modals/ReceiptScanModal';
import apiService from '../services/api';

const Sidebar = ({ sidebarOpen, setSidebarOpen, activeTab, setActiveTab, user, onLogout }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showFinanceSubmenu, setShowFinanceSubmenu] = useState(false);
  const [isReceiptScanModalOpen, setIsReceiptScanModalOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [navOrder, setNavOrder] = useState(['dashboard', 'finance', 'add']);
  const [draggedItem, setDraggedItem] = useState(null);
  const longPressTimer = useRef(null);
  const desktopUserMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Load accounts for receipt scanning
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const accountsData = await apiService.getAccounts();
        setAccounts(accountsData);
      } catch (err) {
        console.error('Error loading accounts:', err);
      }
    };
    loadAccounts();
  }, []);

  // Load saved navigation order from localStorage, migrating away from any
  // retired tabs (wellness/food/eastereggs) and guaranteeing Home is present.
  useEffect(() => {
    const savedOrder = localStorage.getItem('mobileNavOrder');
    if (!savedOrder) return;
    try {
      let parsed = JSON.parse(savedOrder).filter(
        (item) => !['wellness', 'food', 'eastereggs'].includes(item)
      );
      if (!parsed.includes('dashboard')) {
        parsed.unshift('dashboard'); // Home leads
      }
      setNavOrder(parsed);
      localStorage.setItem('mobileNavOrder', JSON.stringify(parsed));
    } catch (e) {
      console.error('Error loading nav order:', e);
    }
  }, []);

  // Save navigation order to localStorage
  const saveNavOrder = (order) => {
    setNavOrder(order);
    localStorage.setItem('mobileNavOrder', JSON.stringify(order));
  };

  // Long-press handlers for edit mode
  const handleLongPressStart = (e) => {
    // Prevent default to avoid text selection
    e.preventDefault();

    // Start timer for long press detection (600ms)
    longPressTimer.current = setTimeout(() => {
      // Vibrate if available (haptic feedback)
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      setIsEditMode(true);
    }, 600);
  };

  const handleLongPressEnd = () => {
    // Clear timer if touch/mouse is released before timeout
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleLongPressCancel = () => {
    // Clear timer if touch moves (user is scrolling, not long-pressing)
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, itemId) => {
    setDraggedItem(itemId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) return;

    const currentIndex = navOrder.indexOf(draggedItem);
    const targetIndex = navOrder.indexOf(targetId);

    const newOrder = [...navOrder];
    newOrder.splice(currentIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    saveNavOrder(newOrder);
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideDesktop = desktopUserMenuRef.current && !desktopUserMenuRef.current.contains(event.target);

      if (isOutsideDesktop) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChangePassword = () => {
    setShowUserMenu(false);
    setActiveTab('profile');
  };

  const handleViewProfile = () => {
    setShowUserMenu(false);
    setActiveTab('profile');
  };

  // Home leads. Easter-egg tabs (dad jokes / cat / smart quotes / chicken) and
  // the price/portfolio Alerts tab are intentionally not surfaced here — their
  // components remain in the codebase as modules, just unlinked from the menu.
  const navItems = [
    {
      id: 'dashboard',
      label: 'Home',
      icon: BarChart3,
      description: 'Overview and summary'
    },
    {
      id: 'accounts',
      label: 'Accounts',
      icon: Wallet,
      description: 'Brokerage and bank accounts'
    },
    {
      id: 'transactions',
      label: 'Transactions',
      icon: ArrowRightLeft,
      description: 'All transactions from all accounts'
    },
    {
      id: 'receipts',
      label: 'Receipts',
      icon: Receipt,
      description: 'Receipt history and scanning'
    },
    {
      id: 'categories',
      label: 'Categories',
      icon: Tag,
      description: 'Transaction categories'
    },
    {
      id: 'recurring',
      label: 'Recurring',
      icon: Repeat,
      description: 'Recurring payments and subscriptions'
    }
  ];

  const adminItems = [
    {
      id: 'users',
      label: 'Users',
      icon: Users,
      description: 'User management'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      description: 'System configuration'
    }
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    localStorage.setItem('activeTab', tabId);
  };

  return (
    <>
      {/* Desktop Sidebar - Hidden on mobile */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64 bg-gray-900 dark:bg-gray-950">
          <div className="flex items-center justify-between h-16 px-4 bg-gray-800 dark:bg-gray-900">
            <div className="flex items-center">
              <img src="/favicon.svg" alt="FiGuru" className="h-8 w-8 rounded-md" />
              <div className="ml-2">
                <span className="text-xl font-bold bg-gradient-to-br from-sky-400 to-violet-400 bg-clip-text text-transparent">FiGuru</span>
                <span className="ml-2 text-xs text-gray-400">v1.0</span>
              </div>
            </div>
          </div>

          <nav className="mt-5 px-2 flex-1 overflow-y-auto flex flex-col">
          {/* Main Navigation */}
          <div className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-gray-800 dark:bg-gray-800 text-white'
                      : 'text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white dark:hover:text-white'
                  }`}
                  title={item.description}
                >
                  <Icon
                    className={`mr-3 flex-shrink-0 h-5 w-5 transition-colors ${
                      isActive ? 'text-green-400 dark:text-green-500' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-300 dark:group-hover:text-gray-400'
                    }`}
                  />
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* Push everything below to the bottom */}
          <div className="mt-auto">
            {/* Divider */}
            <div className="border-t border-gray-700 dark:border-gray-800 my-4"></div>

            {/* Admin Section - Only visible for admin users */}
            {user?.role === 'Administrator' && (
              <div className="space-y-1 mb-4">
                <div className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  Administration
                </div>
                {adminItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleTabChange(item.id)}
                      className={`group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-gray-800 dark:bg-gray-800 text-white'
                          : 'text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white dark:hover:text-white'
                      }`}
                      title={item.description}
                    >
                      <Icon
                        className={`mr-3 flex-shrink-0 h-5 w-5 transition-colors ${
                          isActive ? 'text-green-400 dark:text-green-500' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-300 dark:group-hover:text-gray-400'
                        }`}
                      />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* User Menu at Bottom */}
            <div className="p-4 bg-gray-800 dark:bg-gray-900" ref={desktopUserMenuRef}>
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="group flex items-center w-full px-2 py-2 text-sm font-medium text-gray-300 dark:text-gray-400 rounded-md hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white dark:hover:text-white transition-colors"
              >
                <User className="mr-3 flex-shrink-0 h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-300 dark:group-hover:text-gray-400" />
                <div className="flex-1 text-left">
                  <div className="text-sm">{user?.full_name || user?.username}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-600">{user?.role}</div>
                </div>
                {showUserMenu ? (
                  <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                )}
              </button>

              {/* User Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700">
                  <div className="py-1">
                    <button
                      onClick={handleViewProfile}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                    >
                      <User className="mr-3 h-4 w-4" />
                      View Profile
                    </button>
                    <button
                      onClick={handleChangePassword}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                    >
                      <Settings className="mr-3 h-4 w-4" />
                      Change Password
                    </button>
                    <div className="border-t border-gray-100 dark:border-gray-700"></div>
                    <button
                      onClick={onLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-900 dark:hover:text-red-300"
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          </div>
          </nav>
        </div>
      </div>

      {/* Mobile Top Header - Only visible on mobile */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-gray-900 dark:bg-gray-950 border-b border-gray-800 dark:border-gray-900 z-50">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center">
            <img src="/favicon.svg" alt="FiGuru" className="h-6 w-6 rounded-md" />
            <div className="ml-2">
              <span className="text-lg font-bold bg-gradient-to-br from-sky-400 to-violet-400 bg-clip-text text-transparent">FiGuru</span>
              <span className="ml-2 text-xs text-gray-400">v1.0</span>
            </div>
          </div>
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="text-gray-400 dark:text-gray-500 hover:text-white dark:hover:text-white"
          >
            {showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Slide-Out Menu - Only visible on mobile */}
      {showMobileMenu && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowMobileMenu(false)}
          />
          {/* Menu Drawer */}
          <div
            ref={mobileMenuRef}
            className="lg:hidden fixed top-14 right-0 bottom-0 w-64 bg-gray-900 dark:bg-gray-950 border-l border-gray-800 dark:border-gray-900 z-50 transform transition-transform duration-300 ease-in-out"
          >
            <nav className="flex flex-col h-full">
              {/* User Profile Section */}
              <div className="p-4 bg-gray-800 dark:bg-gray-900 border-b border-gray-700 dark:border-gray-800">
                <div className="flex items-center mb-3">
                  <User className="h-10 w-10 text-gray-400 dark:text-gray-500" />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-white">{user?.full_name || user?.username}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">{user?.role}</div>
                  </div>
                </div>
                <div className="space-y-1">
                  <button
                    onClick={() => {
                      handleViewProfile();
                      setShowMobileMenu(false);
                    }}
                    className="flex items-center w-full px-3 py-2 text-sm text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white dark:hover:text-white rounded-md transition-colors"
                  >
                    <User className="mr-3 h-4 w-4" />
                    View Profile
                  </button>
                  <button
                    onClick={() => {
                      handleChangePassword();
                      setShowMobileMenu(false);
                    }}
                    className="flex items-center w-full px-3 py-2 text-sm text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white dark:hover:text-white rounded-md transition-colors"
                  >
                    <Settings className="mr-3 h-4 w-4" />
                    Change Password
                  </button>
                  <button
                    onClick={onLogout}
                    className="flex items-center w-full px-3 py-2 text-sm text-red-400 dark:text-red-400 hover:bg-red-900/20 dark:hover:bg-red-900/20 hover:text-red-300 dark:hover:text-red-300 rounded-md transition-colors"
                  >
                    <LogOut className="mr-3 h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              </div>

              {/* Menu Items */}
              <div className="space-y-1 px-2 py-4">
                {/* Categories */}
                <button
                  onClick={() => {
                    handleTabChange('categories');
                    setShowMobileMenu(false);
                  }}
                  className={`group flex items-center w-full px-3 py-3 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'categories'
                      ? 'bg-gray-800 dark:bg-gray-800 text-white'
                      : 'text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white dark:hover:text-white'
                  }`}
                >
                  <Tag className={`mr-3 flex-shrink-0 h-5 w-5 ${
                    activeTab === 'categories' ? 'text-green-400 dark:text-green-500' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-300 dark:group-hover:text-gray-400'
                  }`} />
                  Categories
                </button>

                {/* Settings - Admin only */}
                {user?.role === 'Administrator' && (
                  <button
                    onClick={() => {
                      handleTabChange('settings');
                      setShowMobileMenu(false);
                    }}
                    className={`group flex items-center w-full px-3 py-3 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'settings'
                        ? 'bg-gray-800 dark:bg-gray-800 text-white'
                        : 'text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white dark:hover:text-white'
                    }`}
                  >
                    <Settings className={`mr-3 flex-shrink-0 h-5 w-5 ${
                      activeTab === 'settings' ? 'text-green-400 dark:text-green-500' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-300 dark:group-hover:text-gray-400'
                    }`} />
                    Settings
                  </button>
                )}

                {/* Users - Admin only */}
                {user?.role === 'Administrator' && (
                  <button
                    onClick={() => {
                      handleTabChange('users');
                      setShowMobileMenu(false);
                    }}
                    className={`group flex items-center w-full px-3 py-3 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'users'
                        ? 'bg-gray-800 dark:bg-gray-800 text-white'
                        : 'text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white dark:hover:text-white'
                    }`}
                  >
                    <Users className={`mr-3 flex-shrink-0 h-5 w-5 ${
                      activeTab === 'users' ? 'text-green-400 dark:text-green-500' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-300 dark:group-hover:text-gray-400'
                    }`} />
                    Users
                  </button>
                )}
              </div>
            </nav>
          </div>
        </>
      )}

      {/* Mobile Bottom Navigation - Only visible on mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-900 dark:bg-gray-950 border-t border-gray-800 dark:border-gray-900 z-50">
        {/* Edit Mode Overlay - Shows when in edit mode */}
        {isEditMode && (
          <div className="absolute inset-x-0 -top-12 flex items-center justify-center">
            <div className="bg-green-600 text-white px-4 py-2 rounded-t-lg shadow-lg flex items-center gap-2">
              <Settings className="h-4 w-4 animate-pulse" />
              <span className="text-sm font-medium">Drag to rearrange</span>
              <button
                onClick={() => setIsEditMode(false)}
                className="ml-2 px-3 py-1 bg-white text-green-600 rounded-md text-xs font-semibold hover:bg-gray-100 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}

        <nav className="flex justify-around items-center h-16 px-2">
          {navOrder.map((itemId) => {
            const effectiveItemId = itemId;
            const isDragging = draggedItem === itemId;

            // Render different items based on ID
            if (effectiveItemId === 'finance') {
              return (
                <div
                  key={itemId}
                  className="relative flex-1 h-full"
                  draggable={isEditMode}
                  onDragStart={(e) => handleDragStart(e, itemId)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, itemId)}
                  onDragEnd={handleDragEnd}
                >
                  <button
                    onClick={() => !isEditMode && setShowFinanceSubmenu(!showFinanceSubmenu)}
                    onTouchStart={!isEditMode ? handleLongPressStart : undefined}
                    onTouchEnd={!isEditMode ? handleLongPressEnd : undefined}
                    onTouchMove={!isEditMode ? handleLongPressCancel : undefined}
                    onMouseDown={!isEditMode ? handleLongPressStart : undefined}
                    onMouseUp={!isEditMode ? handleLongPressEnd : undefined}
                    onMouseLeave={!isEditMode ? handleLongPressCancel : undefined}
                    disabled={isEditMode}
                    className={`flex flex-col items-center justify-center w-full h-full transition-colors ${
                      isDragging ? 'opacity-50' : ''
                    } ${
                      ['accounts', 'transactions', 'receipts'].includes(activeTab) ? 'text-pink-400 dark:text-pink-500' : 'text-gray-400 dark:text-gray-500'
                    }`}
                    title="Finance"
                  >
                    <Wallet className="h-6 w-6" />
                    <span className="text-xs mt-1">Finance</span>
                  </button>
                  {/* Finance Submenu */}
                  {showFinanceSubmenu && !isEditMode && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowFinanceSubmenu(false)}
                      />
                      <div className="absolute bottom-full left-0 mb-2 w-48 bg-gray-800 dark:bg-gray-900 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 z-50">
                        <div className="py-1">
                          <button
                            onClick={() => {
                              handleTabChange('accounts');
                              setShowFinanceSubmenu(false);
                            }}
                            className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${
                              activeTab === 'accounts'
                                ? 'bg-gray-700 dark:bg-gray-800 text-white'
                                : 'text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white dark:hover:text-white'
                            }`}
                          >
                            <Wallet className="mr-3 h-4 w-4" />
                            Accounts
                          </button>
                          <button
                            onClick={() => {
                              handleTabChange('transactions');
                              setShowFinanceSubmenu(false);
                            }}
                            className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${
                              activeTab === 'transactions'
                                ? 'bg-gray-700 dark:bg-gray-800 text-white'
                                : 'text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white dark:hover:text-white'
                            }`}
                          >
                            <ArrowRightLeft className="mr-3 h-4 w-4" />
                            Transactions
                          </button>
                          <button
                            onClick={() => {
                              handleTabChange('receipts');
                              setShowFinanceSubmenu(false);
                            }}
                            className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${
                              activeTab === 'receipts'
                                ? 'bg-gray-700 dark:bg-gray-800 text-white'
                                : 'text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white dark:hover:text-white'
                            }`}
                          >
                            <Receipt className="mr-3 h-4 w-4" />
                            Receipts
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            }

            if (effectiveItemId === 'dashboard') {
              return (
                <button
                  key={itemId}
                  onClick={() => !isEditMode && handleTabChange('dashboard')}
                  onTouchStart={!isEditMode ? handleLongPressStart : undefined}
                  onTouchEnd={!isEditMode ? handleLongPressEnd : undefined}
                  onTouchMove={!isEditMode ? handleLongPressCancel : undefined}
                  onMouseDown={!isEditMode ? handleLongPressStart : undefined}
                  onMouseUp={!isEditMode ? handleLongPressEnd : undefined}
                  onMouseLeave={!isEditMode ? handleLongPressCancel : undefined}
                  disabled={isEditMode}
                  draggable={isEditMode}
                  onDragStart={(e) => handleDragStart(e, itemId)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, itemId)}
                  onDragEnd={handleDragEnd}
                  className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                    isDragging ? 'opacity-50' : ''
                  } ${
                    activeTab === 'dashboard' ? 'text-pink-400 dark:text-pink-500' : 'text-gray-400 dark:text-gray-500'
                  }`}
                  title="Home"
                >
                  <BarChart3 className="h-6 w-6" />
                  <span className="text-xs mt-1">Home</span>
                </button>
              );
            }

            if (effectiveItemId === 'add') {
              return (
                <div
                  key={itemId}
                  className="flex-1 flex items-center justify-center"
                  draggable={isEditMode}
                  onDragStart={(e) => handleDragStart(e, itemId)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, itemId)}
                  onDragEnd={handleDragEnd}
                >
                  <button
                    onClick={() => {
                      if (!isEditMode) {
                        setIsReceiptScanModalOpen(true);
                      }
                    }}
                    onTouchStart={!isEditMode ? handleLongPressStart : undefined}
                    onTouchEnd={!isEditMode ? handleLongPressEnd : undefined}
                    onTouchMove={!isEditMode ? handleLongPressCancel : undefined}
                    onMouseDown={!isEditMode ? handleLongPressStart : undefined}
                    onMouseUp={!isEditMode ? handleLongPressEnd : undefined}
                    onMouseLeave={!isEditMode ? handleLongPressCancel : undefined}
                    disabled={accounts.length === 0 || isEditMode}
                    className={`flex items-center justify-center w-14 h-14 -mt-8 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed ${
                      isDragging ? 'opacity-50' : ''
                    }`}
                    title="Add Receipt"
                  >
                    <Plus className="h-8 w-8" strokeWidth={2.5} />
                  </button>
                </div>
              );
            }

            return null;
          })}
        </nav>
      </div>

      {/* Receipt Scan Modal */}
      {accounts.length > 0 && (
        <ReceiptScanModal
          isOpen={isReceiptScanModalOpen}
          onClose={() => setIsReceiptScanModalOpen(false)}
          account={accounts[0]}
          onItemsExtracted={() => {
            setIsReceiptScanModalOpen(false);
            // Optionally navigate to receipts tab
            handleTabChange('receipts');
          }}
        />
      )}

    </>
  );
};

export default Sidebar;